import {Component, OnInit, Input} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormGroup, UntypedFormControl, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import {AuthenticationService} from '@app/auth';
import { IInputProps } from '@shared/components/input/input.component';
import { ITextAreaProps } from '@shared/models/shared.models';
import {VendorIntegrationService} from '@shared/services/vendor-integration.service';
import {environment} from '@env/environment';
import {IUserProfile} from '@app/store/Models/settings/settingsModel';
import {selectUserProfile} from '@app/store/Selectors/workspace/workspace.selectors';
import {Store} from '@ngrx/store';
import {tap, catchError} from 'rxjs/operators';
import {saveUserProfile} from '@app/store/Actions/settings/settings.actions';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {of} from 'rxjs';
import {DialogService} from '@shared/services/dialog/dialog.service';

@UntilDestroy()
@Component({
  selector: 'app-social-user-request-modal',
  templateUrl: './social-user-request-modal.component.html',
  styleUrl: './social-user-request-modal.component.scss',
  standalone: false
})
export class SocialUserRequestModalComponent implements OnInit {
  @Input() reportTitle: string;
  requestAccessFormModal: UntypedFormGroup;

  inputProps: { [key: string]: IInputProps } = {
    Email: {
      class: 'form-control uui-modal-input input-error',
      placeholder: 'Corporate Email',
      label: 'Corporate Email *',
    },
    FirstName: {
      class: 'form-control uui-modal-input input-error',
      placeholder: 'First Name',
      label: 'First Name *',
      disabled: true,
    },
    LastName: {
      class: 'form-control uui-modal-input',
      placeholder: 'Last Name',
      label: 'Last Name *',
      disabled: true,
    },
    PhoneNumber: {
      class: 'form-control uui-modal-input not-include-country-code',
      placeholder: 'Phone Number',
      label: 'Phone Number *',
    },
  };

  inputCommentProps: ITextAreaProps = {
    name: 'topic',
    class: 'form-control uui-modal-input',
    label: 'Optional message',
    rows: 2,
    cols: 50,
    minLength: 1,
    maxLength: 500,
  };

  private readonly userProfile$ = this.store.select(selectUserProfile);

  constructor(
    public modal: NgbActiveModal,
    private readonly authService: AuthenticationService,
    private readonly vendorIntegrationService: VendorIntegrationService,
    private readonly store: Store,
    private readonly dialogService: DialogService,
  ) {}

  ngOnInit() {
    this.requestAccessFormModal = new UntypedFormGroup({
      FirstName: new UntypedFormControl('', Validators.required),
      LastName: new UntypedFormControl('', Validators.required),
      Email: new UntypedFormControl('', [Validators.required, Validators.email]),
      PhoneNumber: new UntypedFormControl('', [Validators.required, this.tenDigitPhone]),
      Comment: new UntypedFormControl(''),
    });

    this.authService.getProfile().subscribe((profile ) => {
      this.setFieldValue('FirstName', profile.firstName, '*J', this.inputProps.firstName);
      this.setFieldValue('LastName', profile.lastName, '*Doe', this.inputProps.lastName);
    });
  }

  updateControlValidity(controlName: string) {
    const control = this.requestAccessFormModal.controls[controlName];
    if (controlName === 'PhoneNumber') {
      const formatted = this.formatPhoneNumber(control.value || '');
      control.setValue(formatted, { emitEvent: false });
    }
    control.updateValueAndValidity();
    control.markAsTouched();

    const inputProps = this.inputProps[controlName];
    if (inputProps) {
      inputProps.label = inputProps.label.replace('*', '').replace('is required', '').trim();
    }
  }

  onModalClose() {
    this.modal.close();
  }

  dismissModal() {
    this.modal.dismiss('close');
  }

  handleSendInquiry() {
    this.requestAccessFormModal.updateValueAndValidity();
    if (!this.requestAccessFormModal.valid) return;

    this.userProfile$
      .pipe(
        tap(userProfile => this.updateUserProfile(userProfile)),
        catchError(() => {
          this.openErrorModal();
          return of(null);
        }),
        untilDestroyed(this),
      )
      .subscribe(userProfile => {
        if (userProfile) {
          this.dismissModal();
          this.showSuccessModal();
          this.submitInquiryForm(userProfile);
        }
      }, () => {
        this.openErrorModal();
      });
  }

  submitInquiryForm(profile: IUserProfile) {
    const body = {
      username: profile.email,
      phone: profile.phone || this.onlyDigits(this.requestAccessFormModal.get('PhoneNumber')!.value),
      corporateemail: this.requestAccessFormModal.get('Email')!.value,
      firstname: profile.firstName,
      lastname: profile.lastName,
      comment: this.requestAccessFormModal.get('Comment')!.value,
      requestedItem: this.reportTitle,
    };
    this.vendorIntegrationService
      .submitPardotForm(environment.pardotForms.socialEmailInquiryForm, body)
      .subscribe(() => this.onModalClose(), () => {
        this.onModalClose();
        this.openErrorModal();
      });
  }

  setFieldValue(field: string, value: string, invalidValue: string, inputProps: IInputProps): void {
    const formField = this.requestAccessFormModal.get(field);
    if (value === invalidValue) {
      inputProps.disabled = false;
    } else {
      formField?.setValue(value);
    }
  }

  private updateUserProfile(userProfile: IUserProfile) {
    const updatedProfile = {
      ...userProfile,
      firstName: this.requestAccessFormModal.get('FirstName')!.value,
      lastName: this.requestAccessFormModal.get('LastName')!.value,
      phone: this.onlyDigits(this.requestAccessFormModal.get('PhoneNumber')!.value),
    };
    return this.store.dispatch(saveUserProfile({ userProfile: updatedProfile }));
  }

  private showSuccessModal() {
    const modalServiceRef = this.dialogService.openGeneric({
      title: 'Entitlement Request sent!',
      body: 'Our team has received your request and will process your inquiry shortly. We are looking forward to helping you!',
      closeButtonLabel: 'Done',
    });
    modalServiceRef.result.then(() => {});
  }

  private openErrorModal() {
    this.dialogService.openError().result.then();
  }

  private onlyDigits(v: string): string {
    return (v || '').replace(/\D/g, '');
  }

  private formatPhoneNumber(v: string): string {
    const d = this.onlyDigits(v).slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
    return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  }

  private readonly tenDigitPhone: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const digits = (control.value || '').toString().replace(/\D/g, '');
    return digits.length === 10 ? null : { phoneInvalid: true };
  };
}
